import React from "react";
import {
  TextField,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  // OutlinedInput, // Removed for single select
  // Checkbox, // Removed for single select
  // ListItemText, // Removed for single select
  Typography,
  Divider,
  Box,
  Slider,
  Input,
  InputAdornment,
  IconButton,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";

// This list should be kept consistent with CategoryPopup.jsx
const CrewSelectItems = [
  "Director",
  "1st Assistant Director",
  "2nd Assistant Director",
  "Line Producer",
  "Production Manager",
  "Production Coordinator",
  "Production Secretary",
  "Production Assistant",
  "Script Supervisor",
  "Stunt Coordinator",
  "Story Producer",
  "Location Manager",
  "Location Assistant",
  "Location Scout",
  "Director of Photography",
  "Camera Operator",
  "First Assistant Camera",
  "Second Assistant Camera",
  "Camera Production Assistant",
  "Digital Imaging Technician",
  "Data Wrangler",
  "Steadicam Operator",
  "Production Sound Mixer",
  "Boom Operator",
  "Key Grip",
  "Best Boy (Grip)",
  "Dolly Grip",
  "Gaffer",
  "Best Boy (Electrical)",
  "Lighting Technician",
  "Production Designer",
  "Art Director",
  "Set Designer",
  "Set Decorator",
  "Buyer",
  "Set Dresser",
  "Props Master",
  "Standby Props",
  "Props Builder",
  "Make-up Artist",
  "SFX Make-up Artist",
  "Hairdresser",
  "Costume Designer",
  "Costume Supervisor",
  "Cutter/Fitter",
  "Film Editor",
  "Assistant Editor",
  "Online Editor",
  "Colourist",
  "Negative Cutter",
  "Best Boy",
  "Boom Operator",
  "Camera Assistant",
  "Camera Operator",
  "Caterer",
  "Cinematographer",
  "Costume Coordinator",
  "Costume Designer",
  "COVID Compliance Officer",
  "Crafts and Services",
  "Director of Photography",
  "Dolly Grip",
  "Driver",
  "Electrician",
  "Film Editor",
  "Gaffer",
  "Generator Operator",
  "Grip",
  "Hair Stylist",
  "Intimacy Coordinator",
  "Key PA",
  "Lead Man",
  "Line Producer",
  "Location Scout",
  "Makeup Artist",
  "Officer PA",
  "On Set Editor",
  "Production Accountant",
  "Production Assistance",
  "Production Designer",
  "Prop Master",
  "Prosthetics Designer",
  "Screenwriter",
  "Script Supervisor",
  "Set Decorator",
  "Set Dresser",
  "Set Medic",
  "Set PA",
  "SFX Coordinator",
  "SFX Editor",
  "SFX Makeup Artist",
  "SFX Supervisor",
  "Shopper",
  "Sound Mixer",
  "Stunt Choreographer",
  "Stunt Performer",
  "Tailor",
  "Transportation Coordinator",
  "Unit Production Manager",
  "Video Assist Operator",
  "Weapons Wrangler"
];

const Genders = ["Male", "Female", "Non-Binary", "Transgender"];

const ageMarks = [
  {
    value: 18,
    label: "18",
  },
  {
    value: 100,
    label: "100",
  },
];

function ageText(value) {
  return `${value}`;
}

const CrewSearchFilter = ({ searchInputs, setSearchInputs, handleSearch, ageValue, handleAgeChange }) => {
  const theme = useTheme();

  const handleInputChange = (name, value) => {
    setSearchInputs((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleJobTitleChange = (event) => {
    const value = event.target.value;
    setSearchInputs((prevDetails) => ({
      ...prevDetails,
      jobTitles: value, // Now a single string
    }));
    handleSearch({ // Apply filter immediately
      ...searchInputs,
      jobTitles: value,
    });
  };

  const handleGenderChange = (genderValue) => (e) => {
    const newGenders = e.target.checked
      ? [...(searchInputs.gender || []), genderValue]
      : (searchInputs.gender || []).filter((v) => v !== genderValue);

    setSearchInputs((prevDetails) => ({
      ...prevDetails,
      gender: newGenders,
    }));
    handleSearch({ // Apply filter immediately
      ...searchInputs,
      gender: newGenders,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        minWidth: "15%",
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Search & Filters</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControl sx={{ mb: 2 }} fullWidth variant="filled">
          <InputLabel htmlFor="search-keyword-input">
            Search Keyword
          </InputLabel>
          <Input
            id="search-keyword-input"
            value={searchInputs.name}
            onChange={(e) => {
              handleInputChange("name", e.target.value);
              handleSearch({ ...searchInputs, name: e.target.value });
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={() => handleSearch(searchInputs)}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Job Titles</Typography>
        <Divider sx={{ mb: 2 }} />
        <FormControl fullWidth variant="filled">
          <InputLabel id="job-titles-label">Job Title</InputLabel>
          <Select
            labelId="job-titles-label"
            id="job-titles-select"
            value={searchInputs.jobTitles || ''} // Now a single string
            onChange={handleJobTitleChange}
            MenuProps={{
              // disableScrollLock: true, // Keep this if needed
              PaperProps: {
                style: {
                  maxHeight: 250, // Set max height for scrolling
                  width: 250, // Set a consistent width
                },
              },
            }}
          >
            <MenuItem value="">Any</MenuItem> {/* Option to clear selection */}
            {CrewSelectItems.map((jobTitle) => (
              <MenuItem key={jobTitle} value={jobTitle}>
                {jobTitle}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Gender</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {Genders.map((gender) => (
            <Box
              key={gender}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Checkbox
                sx={{ padding: 0, marginRight: "5px" }}
                color="secondary"
                checked={(searchInputs.gender || []).includes(
                  gender.toLowerCase()
                )}
                onChange={handleGenderChange(
                  gender.toLowerCase()
                )}
              />
              <Typography variant="caption">{gender}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Age (Years)</Typography>
        <Divider sx={{ mb: 2 }} />
        <Slider
          getAriaLabel={() => "Age Range"}
          value={ageValue}
          onChange={handleAgeChange}
          valueLabelDisplay="auto"
          getAriaValueText={ageText}
          marks={ageMarks}
          sx={{
            marginLeft: "10px",
            marginTop: "10px",
            width: "200px",
            color: theme.palette.secondary.main,
            "& .MuiSlider-rail": {
              backgroundColor: "#e0e0e0",
              opacity: 1,
            },
            "& .MuiSlider-track": {
              background: `linear-gradient(to bottom, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
              border: "none",
            },
            "& .MuiSlider-thumb": {
              width: 20,
              height: 20,
              backgroundColor: theme.palette.background.paper,
              border: `2px solid ${theme.palette.secondary.main}`,
              boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.4)",
              },
            },
            "& .MuiSlider-mark": {
              backgroundColor: "#bfbfbf",
              height: 8,
              width: 2,
              "&.MuiSlider-markActive": {
                backgroundColor: theme.palette.secondary.main,
              },
            },
            "& .MuiSlider-markLabel": {
              transform: "translateX(-5px)",
              color: theme.palette.text.secondary,
              fontSize: "0.875rem",
              fontWeight: 500,
            },
            "& .MuiSlider-valueLabel": {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.background.paper,
              borderRadius: "8px",
              padding: "4px 8px",
              fontSize: "0.875rem",
              fontWeight: 600,
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              "&:before": {
                display: "none",
              },
            },
          }}
          color="secondary"
        />
      </Box>
    </Box>
  );
};

export default CrewSearchFilter;
